import { useWorkspace } from "@/hooks/useWorkspace";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Building2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface WorkspaceAvatarProps {
  size?: "sm" | "md" | "lg";
  showName?: boolean;
  className?: string;
}

export const WorkspaceAvatar = ({ size = "md", showName = false, className = "" }: WorkspaceAvatarProps) => {
  const { workspace, loading } = useWorkspace();

  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
  };

  const iconSizes = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Skeleton className={`${sizeClasses[size]} rounded-full`} />
        {showName && <Skeleton className="h-4 w-24" />}
      </div>
    );
  }

  if (!workspace) return null;

  const logoUrl = workspace.branding_settings?.logo_url;
  const workspaceName = workspace.name || "Workspace";
  const workspaceInitials = workspaceName
    .split(" ")
    .map(word => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Avatar className={sizeClasses[size]}>
        {logoUrl && (
          <AvatarImage src={logoUrl} alt={workspaceName} className="object-cover" />
        )}
        <AvatarFallback className="bg-primary/10 text-primary">
          <Building2 className={iconSizes[size]} />
        </AvatarFallback>
      </Avatar>
      {showName && (
        <span className="text-sm font-medium text-foreground truncate max-w-[150px]">
          {workspaceName}
        </span>
      )}
    </div>
  );
};
